import { FC } from "react";
import { ROLES } from "types/user";
import { useCustomUserList } from "./hook";
import CustomTable from "components/Table/CustomTable";
import { StrugglingFlagChip } from "components/StrugglingFlag/StrugglingFlagChip";
import { useStrugglingLearnerRoster } from "hooks/useStrugglingLearnerRoster";
import {
  Box,
  Button,
  Stack,
  Link as LinkBase,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminRoutes, CoachRoutes } from "config/routes";
import { MainLayoutContainer } from "components/Layout";
import { TABLE_ACTIONS } from "../../../helpers/constants";
import { Feedback } from "@mui/icons-material";
import { filterUserByNameOrEmail } from "../userTableFilters";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";

type CustomUserListProps = {
  role: ROLES;
};

export const CustomUserList: FC<CustomUserListProps> = ({ role }) => {
  const { users, loading, error, userRole } = useCustomUserList(role);
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const canViewStrugglingFlags =
    role === ROLES.STUDENT &&
    (userRole === ROLES.ADMIN ||
      userRole === ROLES.DIRECTOR ||
      userRole === ROLES.MANAGER);
  const { strugglingByStudentId, strugglingLoading } =
    useStrugglingLearnerRoster(
      canViewStrugglingFlags ? organizationId : undefined,
    );

  const buildViewPath = (userId: string) => {
    if (userRole === ROLES.COACH) {
      return CoachRoutes.LearnerDetail.replace(":userId", userId);
    }
    return AdminRoutes.OrganizationLearnerDetail.replace(
      ":organizationId",
      organizationId ?? "",
    ).replace(":userId", userId);
  };

  function getExtraActionItems(role: ROLES | undefined) {
    if (role === ROLES.COACH) {
      return [
        {
          name: TABLE_ACTIONS.GIVE_FEEDBACK,
          icon: <Feedback />,
        },
      ];
    }
    if (role === ROLES.ADMIN) {
      return [
        {
          name: TABLE_ACTIONS.REQUEST_FEEDBACK,
          icon: <Feedback />,
        },
      ];
    }
    return [];
  }

  function getColumns(role: ROLES | undefined) {
    const base = [
      {
        id: "name",
        label: "Name",
        value: (row: any) => (
          <Stack direction="row" spacing={2} alignItems={"center"}>
            <UserWithPet
              pictureUrl={row.picture_url}
              name={row.name}
              equippedStoreItems={row.equipped_store_items}
              avatarSize={40}
            />
            <Box>{row.name}</Box>
          </Stack>
        ),
      },
      { id: "email", label: "Email", value: "email" },
      ...(canViewStrugglingFlags
        ? [
            {
              id: "struggling",
              label: "Situation",
              value: (row: any) => (
                <StrugglingFlagChip
                  summary={strugglingByStudentId.get(row.user_id || "")}
                  orgId={organizationId}
                />
              ),
            },
          ]
        : []),
      {
        id: "user_id",
        value: (row: any) => (
          <Stack direction="row" spacing={2} alignItems={"center"}>
            <LinkBase
              sx={{ cursor: "pointer" }}
              component={Link}
              to={buildViewPath(row.user_id)}
            >
              View
            </LinkBase>
          </Stack>
        ),
      },
    ];
    if (role === ROLES.ADMIN || role === ROLES.COACH) {
      return [...base, { id: "actions", value: "" }];
    }
    return base;
  }

  function handleActionClick(action: TABLE_ACTIONS, id: string) {
    switch (action) {
      case TABLE_ACTIONS.GIVE_FEEDBACK:
        navigate(
          CoachRoutes.GiveFeedbackForLearner.replace(":userId", id),
        );
        break;
      case TABLE_ACTIONS.REQUEST_FEEDBACK:
        navigate(`/learner/${id}/request-feedback`);
        break;
    }
  }

  return (
    <MainLayoutContainer
      title={`${role === ROLES.STUDENT ? "Learners" : "Mentors"}`}
      loading={loading || strugglingLoading}
      error={error}
      buttonActions={
        userRole === ROLES.COACH && role === ROLES.STUDENT ? (
          <Button
            variant="contained"
            startIcon={<Feedback />}
            onClick={() => navigate(CoachRoutes.GiveFeedback)}
          >
            Give Feedback
          </Button>
        ) : null
      }
    >
      <CustomTable
        entities={users || []}
        handleActionClick={handleActionClick}
        fieldToFilter={filterUserByNameOrEmail}
        extraActionItems={getExtraActionItems(userRole)}
        allowedMenuItems={
          userRole === ROLES.COACH
            ? [TABLE_ACTIONS.GIVE_FEEDBACK]
            : [TABLE_ACTIONS.REQUEST_FEEDBACK]
        }
        columns={getColumns(userRole)}
        isSelectable={false}
        idColumn={"user_id"}
        enableCardViewInSmallDevices
        enableMenuInSmallDevices={getExtraActionItems(userRole).length > 0}
        renderCardContent={(data: any) => (
          <Box>
            <Stack direction="row" spacing={2} alignItems={"center"} sx={{ minWidth: 0 }}>
              <UserWithPet
                pictureUrl={data.picture_url}
                name={data.name}
                equippedStoreItems={data.equipped_store_items}
                avatarSize={40}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1">{data.name}</Typography>
                {data.email && (
                  <Typography variant="body2" color="secondary">
                    {data.email}
                  </Typography>
                )}
              </Box>
            </Stack>
            {canViewStrugglingFlags && (
              <Box my={1}>
                <Typography variant="body1" component="div">
                  <strong>Situation:</strong>{" "}
                  <StrugglingFlagChip
                    summary={strugglingByStudentId.get(data.user_id || "")}
                    orgId={organizationId}
                  />
                </Typography>
              </Box>
            )}
          </Box>
        )}
      />
    </MainLayoutContainer>
  );
};
